import {
  DestroyRef,
  Directive,
  inject,
  input,
  InputSignal,
  OnInit,
  output,
  OutputEmitterRef,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ControlsOf } from '@api-types/helper/helper';
import { filter, Observable, take } from 'rxjs';

export interface CreateAnalysisConfigurationBaseStep<T> {
  /**
   * @description reactive angular form, content depends on the specific insight configuration component implementation
   */
  form: FormGroup;

  /**
   * @description emits `true | false` whenever the form validity changes
   */
  validChange: OutputEmitterRef<boolean>;

  /**
   * @description emits the current form data whenever the form is valid and the data changes
   */
  dataChange: OutputEmitterRef<T | undefined>;

  initData: InputSignal<T | undefined>;
}

@Directive()
export abstract class CreateAnalysisConfigurationBaseStepComponent<T>
  implements CreateAnalysisConfigurationBaseStep<T>, OnInit
{
  protected readonly fb = inject(FormBuilder);
  public abstract form: FormGroup<ControlsOf<T extends object ? T : never>>;

  readonly initData = input<T | undefined>();

  validChange = output<boolean>();
  dataChange = output<T | undefined>();

  private readonly destroyRef = inject(DestroyRef);

  private readonly initDataAsObservable: Observable<T | undefined>;

  constructor() {
    this.initDataAsObservable = toObservable(this.initData);
  }

  ngOnInit(): void {
    this.initDataAsObservable
      .pipe(
        filter((v) => v != undefined),
        take(1),
      )
      .subscribe((v) => {
        this.form.patchValue(v);
        this.validChange.emit(this.form.valid);
        this.dataChange.emit(this.form.getRawValue() as T);
      });

    // emits validity changes of the form
    this.form.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.validChange.emit(this.form.valid));

    // emit the current form data whenever the form is valid and the data changes
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.dataChange.emit(
          this.form.valid ? (this.form.getRawValue() as T) : undefined,
        );
      });

    queueMicrotask(() => {
      this.validChange.emit(this.form.valid);
      this.dataChange.emit(
        this.form.valid ? (this.form.getRawValue() as T) : undefined,
      );
    });
  }
}
